import { cn } from "cn";
import { InfoIcon, TriangleAlertIcon } from "lucide-react";
import {
	TOOLTIP_DELAY_DURATION,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	type TooltipSide,
	TooltipTrigger,
} from "#/components/Tooltip/Tooltip";

export type InfoTooltipType = "info" | "warning";

type InfoTooltipSize = "small" | "medium";

type InfoTooltipProps = {
	type?: InfoTooltipType;
	size?: InfoTooltipSize;
	ariaLabel?: string;
	side?: TooltipSide;
	children: React.ReactNode;
};

const typeIcon: Record<InfoTooltipType, typeof InfoIcon> = {
	info: InfoIcon,
	warning: TriangleAlertIcon,
};

const typeIconColor: Record<InfoTooltipType, string> = {
	info: "text-content-secondary hover:text-content-primary",
	warning: "text-content-warning",
};

const sizeClasses: Record<InfoTooltipSize, string> = {
	small: "[&_svg]:size-3",
	medium: "[&_svg]:size-4",
};

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
	children,
	type = "info",
	size = "medium",
	ariaLabel = "More info",
	side = "right",
}) => {
	const Icon = typeIcon[type];

	return (
		<TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
			<Tooltip interactive>
				<TooltipTrigger
					type="button"
					aria-label={ariaLabel}
					className={cn(
						"flex items-center justify-center p-0",
						"border-0 border-none bg-transparent cursor-default",
						"transition-colors",
						sizeClasses[size],
						typeIconColor[type],
					)}
				>
					<Icon />
				</TooltipTrigger>
				<TooltipContent side={side} align="center" className="max-w-xs">
					{children}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
};
